import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { LeapV2Student, LeapV2UploadedFile } from '../models/leap-v2-student.model';
import { LeapV2EvidenceUploadComponent } from '../leap-v2-evidence-upload/leap-v2-evidence-upload.component';
import { LeapV2EvidenceService } from '../services/leap-v2-evidence.service';
import { BehaviouralConcernDialogData, BehaviouralConcernDialogResult } from '../models/leap-v2-attendance.model';
import { LeapV2BehaviouralConcern } from '../models/leap-v2-session.model';
import { LeapV2BehaviouralConcernDialogComponent } from '../dialogs/leap-v2-behavioural-concern-dialog/leap-v2-behavioural-concern-dialog.component';
import { alignAttainmentLevels, hasText, isAttainmentSet, isEngagementSet } from '../leap-v2-progress-validation';

@Component({
  selector: 'leap-v2-student-panel',
  templateUrl: './leap-v2-student-panel.component.html',
  styleUrls: ['./leap-v2-student-panel.component.css'],
  standalone: false,
})
export class LeapV2StudentPanelComponent implements OnInit, OnChanges {
  @Input() student: LeapV2Student;
  @Input() objectives: string[] = [];
  @Input() disabled = false;
  // Server-owned: the one concern already raised for this student on this session (null = none). The
  // parent owns the list and refreshes it after each POST, same as attendance (existingAttendance).
  @Input() concern: LeapV2BehaviouralConcern | null = null;
  // False while there is no anchor sessionId yet, or once the report is Approved.
  @Input() canRaiseConcern = true;
  @Output() concernRaised = new EventEmitter<BehaviouralConcernDialogResult>();

  // Files already uploaded to this session, as the server last returned them. Kept apart from the files
  // picked since, which the upload control emits on its own (onEvidenceChanged).
  savedEvidence: LeapV2UploadedFile[] = [];
  @ViewChild(LeapV2EvidenceUploadComponent) evidenceUpload: LeapV2EvidenceUploadComponent;
  // The files picked since load, as the upload control last emitted them.
  private picked: LeapV2UploadedFile[] = [];

  readonly engCommentPlaceholder = '- Factors affecting engagement\n- Strategies and personalisation used';
  readonly progCommentPlaceholder = '- Activities completed\n- Strategies and approaches\n- What went well / what learning challenges were faced';

  get progressCharCount(): number {
    return this.student.progress.objectiveComments.length;
  }

  // Required-field checks driving the inline hints. Evidence and behavioural concern are optional
  // and deliberately have no hint. Rules live in leap-v2-progress-validation (shared with the
  // parent Submit gate).
  isAttainmentMissing(index: number): boolean {
    return !isAttainmentSet(this.student.progress.attainmentLevels[index]);
  }

  get isProgressCommentsMissing(): boolean {
    return !hasText(this.student.progress.objectiveComments);
  }

  get isEngagementMissing(): boolean {
    return !isEngagementSet(this.student.progress.engagement);
  }

  get isEngagementCommentsMissing(): boolean {
    return !hasText(this.student.progress.engagementComments);
  }

  constructor(private dialog: MatDialog, private evidenceService: LeapV2EvidenceService) {}

  /** Opens an uploaded file through the API (its link is api/Evidence/{id}, not a blob URL). */
  openEvidence(event: Event, file: LeapV2UploadedFile): void {
    event.preventDefault();
    this.evidenceService.open(file);
  }

  ngOnInit(): void {
    // Pad (don't clobber) so any pre-loaded attainment values survive.
    this.syncAttainmentToObjectives();
    this.savedEvidence = savedEvidenceOf(this.student);
  }

  ngOnChanges(changes: SimpleChanges): void {
    // A replaced student (e.g. the parent's post-attendance reload swaps in fresh students) does not
    // re-run ngOnInit, so re-pad its attainment array to the objective count or the Submit gate reads
    // undefined levels and never unlocks.
    const studentChange = changes['student'];
    if (studentChange && !studentChange.firstChange) {
      this.syncAttainmentToObjectives();
      // A replaced student is the server's record: the files picked here are uploaded and listed above
      // now, so clear them from the picker rather than show them twice.
      this.savedEvidence = savedEvidenceOf(this.student);
      this.picked = [];
      if (this.evidenceUpload) this.evidenceUpload.files = [];
    }

    const objectivesChange = changes['objectives'];
    if (!objectivesChange || objectivesChange.firstChange) return;

    const previous: string[] = objectivesChange.previousValue ?? [];
    const current: string[] = objectivesChange.currentValue ?? [];
    const levels = this.student.progress.attainmentLevels;

    if (current.length > previous.length) {
      levels.push(...new Array(current.length - previous.length).fill(-1));
      return;
    }
    if (current.length === previous.length) return;

    let removedIndex = previous.length - 1;
    for (let i = 0; i < current.length; i++) {
      if (previous[i] !== current[i]) {
        removedIndex = i;
        break;
      }
    }
    levels.splice(removedIndex, 1);
  }

  private syncAttainmentToObjectives(): void {
    alignAttainmentLevels(this.student.progress, this.objectives.length);
  }

  getObjectiveLabel(index: number): string {
    const text = this.objectives[index] ?? '';
    const truncated = text.length > 55 ? text.slice(0, 55).trimEnd() + '…' : text;
    return `Obj ${index + 1}: ${truncated || `Learning objective ${index + 1}`}`;
  }

  onAttainmentStep(index: number, step: number): void {
    this.student.progress.attainmentLevels[index] = step;
  }

  /**
   * The uploaded files the screen lists. One the tutor has attached again under the same name is left out: the
   * save stores the new copy in its place (40317), so it is listed once, in the picker.
   */
  get shownSavedEvidence(): LeapV2UploadedFile[] {
    return this.savedEvidence.filter(s => !this.picked.some(p => sameName(p, s)));
  }

  // The upload control emits only the files picked since load, so keep the uploaded ones alongside them.
  // They go back to the API by url and are not stored again. The API stores exactly this list (40317): an
  // uploaded file it no longer contains is removed.
  onEvidenceChanged(files: LeapV2UploadedFile[]): void {
    this.picked = files ?? [];
    this.student.progress.evidence = [...this.shownSavedEvidence, ...this.picked];
  }

  // The button is the indicator: it opens the dialog read-only once a concern exists, otherwise it
  // collects one and hands it up to be POSTed — nothing is written to the student model.
  openBehaviouralConcern(): void {
    const data: BehaviouralConcernDialogData = {
      studentName: this.student.name,
      existingConcern: this.concern ? { reason: this.concern.reason, comments: this.concern.comments } : null,
    };
    this.dialog.open(LeapV2BehaviouralConcernDialogComponent, { width: '520px', data })
      .afterClosed()
      .subscribe((result: BehaviouralConcernDialogResult | undefined) => {
        if (result && !this.concern) {
          this.concernRaised.emit(result);
        }
      });
  }
}

/** The student's uploaded evidence: the files the server returned with a link to open. */
function savedEvidenceOf(student: LeapV2Student): LeapV2UploadedFile[] {
  return (student?.progress?.evidence ?? []).filter(f => !!f.url);
}

/** Same file name, ignoring case, as the API matches them. */
function sameName(a: LeapV2UploadedFile, b: LeapV2UploadedFile): boolean {
  return (a?.name ?? '').toLowerCase() === (b?.name ?? '').toLowerCase();
}
